import styled from "@emotion/styled";
import { MenuItem, Tooltip } from "@mui/material";
import { useEffect, useEffectEvent } from "react";

import { FormattedMessage } from "@skilitics/localization";

import { useModalDialog } from "../dialogs/ModalDialogContext";
import { isUserInput } from "../forms/controls/helpers";
import { Icon } from "../icons";
import { ContextMenu } from "./ContextMenu";
import type { ContextAction } from "./types";

const StyledMenuItem = styled(MenuItem)`
  &.disabled {
    cursor: not-allowed;
    pointer-events: all !important;
  }

  .fa-icon-wrapper {
    margin-right: 10px;
  }
`;

type Props = {
  contextActions?: ContextAction[];
  disableKeyboardShortcuts?: boolean;
};

export const ContextActionsMenu = ({ contextActions = [], disableKeyboardShortcuts }: Props) => {
  const modalContext = useModalDialog();

  const optionSelected = (action: ContextAction) => {
    if (action.confirmOptions) {
      openModalDialog(action);
    } else {
      action.onSelected("payload" in action && action.payload ? action.payload : undefined);
    }
  };

  const createOnClickHandler = (index: number) => () => optionSelected(contextActions[index]);

  const openModalDialog = (action: ContextAction) => {
    const id = "confirmationDialog";

    modalContext.openModalDialog(
      {
        ...action.confirmOptions,
        onConfirm: (confirmed: boolean) => {
          if (confirmed) action.onSelected();
          modalContext.closeModalDialog(id);
        },
      },
      id,
    );
  };

  const handleDelKeyPress = useEffectEvent((e: globalThis.KeyboardEvent) => {
    if (disableKeyboardShortcuts) return;
    if (e.key === "Delete" && !isUserInput(e.target)) {
      e.stopPropagation();

      const deleteAction = contextActions.find((a) => !!a.keyboardShortcut);

      if (deleteAction) optionSelected(deleteAction);
    }
  });

  useEffect(() => {
    window.addEventListener("keydown", handleDelKeyPress);

    return () => window.removeEventListener("keydown", handleDelKeyPress);
  }, []);

  return (
    <ContextMenu disabled={contextActions.length === 0} data-test="context-action-menu">
      {contextActions.map(({ disabled, disabledMessage, icon, label }, index) => (
        <Tooltip
          key={index}
          title={disabled ? disabledMessage ? <FormattedMessage messages={disabledMessage} /> : "" : ""}
          placement="right"
        >
          <StyledMenuItem
            onClick={createOnClickHandler(index)}
            className={disabled ? "disabled" : ""}
            disabled={disabled}
            data-test={`context-action-menu-item-${index}`}
          >
            {icon && <Icon icon={icon} />} <FormattedMessage messages={label} />
            {disabled && <Icon icon="exclamation-circle" />}
          </StyledMenuItem>
        </Tooltip>
      ))}
    </ContextMenu>
  );
};
